// Age gate on arrival — Soap card-brand review requirement (Tyler Carlin).
//
// A simple 18+ attestation (NOT DOB entry): card-network registration for
// age-restricted merchants expects the site to state the age requirement and
// take an affirmative attestation from visitors; DOB capture from anonymous
// visitors would be a privacy surface with no verification value. Real DOB is
// collected at registration and identity-verified via Persona before money
// moves.
//
// Scope: UNAUTHENTICATED WEB visitors only.
//  - Authenticated users already attested at signup — re-gating them is
//    friction with no compliance value.
//  - Suppressed entirely on native (Capacitor.isNativePlatform()): the app
//    carries the App Store age rating, and SAB-706 established that native
//    must never show consent-style prompts from web content. This gate never
//    renders in the Capacitor webview.
//
// Persistence: localStorage (NOT a cookie — no cookie header, no interaction
// with the SAB-431 consent framework, nothing reintroduced for SAB-706) with
// a 30-day revalidation. A decline is SESSION-ONLY (sessionStorage): a
// mis-click must not brick the browser, and reviewers test the "No" path and
// then the "Yes" path in the same session.

import { Capacitor } from '@capacitor/core';

const ATTEST_KEY = 'age_gate_v1';
const DECLINE_KEY = 'age_gate_declined_v1';
export const REVALIDATE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function isAttestationFresh(raw: string | null, now: number): boolean {
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return now - ts >= 0 && now - ts < REVALIDATE_MS;
}

// SAB-1093/1091: HANDOFF ROUTES — machine-driven returns, not visits.
//
// These render inside an in-app browser (SFSafariViewController / Chrome Custom Tab) opened
// by @capacitor/browser, or in a fresh tab a provider redirected to. That context is a
// SEPARATE storage jar from the Capacitor webview: no Supabase session and empty
// localStorage. So `isAuthenticated` is false and no attestation is on file, and the gate
// fired -- on a user who is signed in, already attested at signup, KYC-verified, and had
// just paid. Adam hit this on prod: completed a PayPal deposit and was dumped into the 18+
// overlay instead of a confirmation. The money was correct; the screen was alarming.
//
// The gate's own premise ("an anonymous visitor arriving at the site") is simply false here.
// Nobody browses to /paypal-return -- a payment provider sends them, mid-flow. Attesting
// again has no compliance value and reads as "your account is unverified" straight after
// paying.
//
// Deliberately a small, exact list rather than a prefix match: a broad rule like
// /^\/(auth|wallet)/ would silence the gate across real pages an anonymous visitor CAN
// browse to, which is the compliance surface Soap's card-brand review asked for.
const HANDOFF_ROUTES = new Set([
  '/paypal-return',   // PayPal returns the buyer here, which forwards to the app scheme
  '/persona-return',  // Persona hosted-flow return
  '/auth/confirm',    // email confirm / change-email landing (token_hash in the URL)
]);

export function isHandoffRoute(pathname: string): boolean {
  // Exact match on the path only. Query strings carry the provider's token and must not
  // affect the decision; a trailing slash is the same route.
  const p = pathname.replace(/\/+$/, '') || '/';
  return HANDOFF_ROUTES.has(p);
}

// Pure decision, unit-tested: should the gate render at all?
export function shouldShowAgeGate(opts: {
  isNative: boolean;
  isAuthenticated: boolean;
  authLoading: boolean;
  storedAttestation: string | null;
  now: number;
  /** Current path. On a handoff route the gate never renders — see HANDOFF_ROUTES. */
  pathname?: string;
}): boolean {
  if (opts.isNative) return false;
  if (opts.pathname !== undefined && isHandoffRoute(opts.pathname)) return false;
  if (opts.authLoading) return false; // never flash the gate while auth resolves
  if (opts.isAuthenticated) return false;
  return !isAttestationFresh(opts.storedAttestation, opts.now);
}

/* Runtime storage helpers (try/catch: storage can be unavailable in some
   privacy modes — fail open to the gate showing, never crash). */

export function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function readAttestation(): string | null {
  try {
    return window.localStorage.getItem(ATTEST_KEY);
  } catch {
    return null;
  }
}

export function writeAttestation(now: number): void {
  try {
    window.localStorage.setItem(ATTEST_KEY, String(now));
  } catch {
    /* storage unavailable — gate will show again next load; acceptable */
  }
}

export function readDeclined(): boolean {
  try {
    return window.sessionStorage.getItem(DECLINE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDeclined(): void {
  try {
    window.sessionStorage.setItem(DECLINE_KEY, '1');
  } catch {
    /* session-only anyway */
  }
}

export function clearDeclined(): void {
  try {
    window.sessionStorage.removeItem(DECLINE_KEY);
  } catch {
    /* ignore */
  }
}
